import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import MeterDetailsBlade from '../MeterDetailsBlade';
import BladeSection from '../BladeSection';
import bladeStyles from './EstimationBlade.module.css';
import { useNavigate } from 'react-router-dom';

export interface MeterEstimateData {
  meterId: string | number;
  meterNumber: string;
  name: string;
  lastMonth: number | null;
  lastMonthMeterReadType: string | null;
  estimate: number | null;
  factor: string | number | null;
  booked: number | null;
  comment: string | null;
  percentChange: number | null;
  approvalStatus: string | null;
  isManualOverride: boolean | null;
}

export interface RegionalRow {
  region: string;
  lastMonth: number;
  currentMonth: number;
  factor: number | null;
}

interface MeterInfo {
  meter_computed_id: number;
  location: string | null;
  meter_id: string | number;
  meter_number: string | null;
  meter_read_type: string | null;
}

export interface EstimationBladeProps {
  open: boolean;
  onClose: () => void;
  meter: MeterInfo | null;
  monthYear: string;
  meterData: MeterEstimateData | null;
  energySource: string | null;
  regionalData: RegionalRow[];
  saving: boolean;
  applyError: string | null;
  onApply: (values: { booked: number; comment: string; isManualOverride: boolean }) => void;
  onMonthChange?: (monthYear: string) => void;
  readOnly?: boolean;
}

const EstimationBlade: React.FC<EstimationBladeProps> = ({
  open,
  onClose,
  meter,
  monthYear,
  meterData,
  energySource,
  regionalData,
  saving,
  applyError,
  onApply,
  onMonthChange,
  readOnly = false,
}) => {
  const [calculatedEstimate, setCalculatedEstimate] = useState('');
  const [bookedEstimate, setBookedEstimate] = useState('');
  const [isManualUserValue, setIsManualUserValue] = useState(false);
  const [comments, setComments] = useState('');
  const [commentError, setCommentError] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!meterData) {
      setCalculatedEstimate('');
      setBookedEstimate('');
      setIsManualUserValue(false);
      setComments('');
      setCommentError(false);
      return;
    }
    const lastMonthKWh = meterData.lastMonth ?? 0;
    const estFactor = Number(String(meterData.factor ?? 0).replace('%', ''));
    const autoCalc = Math.round(lastMonthKWh * (estFactor / 100));
    const calcValue = meterData.estimate
      ? String(Math.round(Number(meterData.estimate)))
      : String(autoCalc);
    setCalculatedEstimate(calcValue);
    setBookedEstimate(meterData.booked ? String(Math.round(Number(meterData.booked))) : '');
    setIsManualUserValue(meterData.isManualOverride ?? false);
    setComments(meterData.comment ?? '');
    setCommentError(false);
  }, [meterData]);

  const estFactor = meterData?.factor ?? 0;
  const lastMonthKWh = meterData?.lastMonth ?? 0;
  const energySourceLabel = energySource
    ? energySource.charAt(0).toUpperCase() + energySource.slice(1)
    : 'Unknown';
  const monthLabel = dayjs(monthYear, 'YYYY-MM').format('MMMM YYYY');
  const displayedEstimate = isManualUserValue ? bookedEstimate : calculatedEstimate;

  const getFactorClassName = (factor: number | null) => {
    if (factor == null) return bladeStyles.factorNeutral;
    if (factor > 100) return bladeStyles.factorPositive;
    if (factor < 100) return bladeStyles.factorNegative;
    return bladeStyles.factorNeutral;
  };

  const handleApplyClick = () => {
    const bookedValue = Number(String(displayedEstimate).replaceAll(',', ''));
    if (Number.isNaN(bookedValue)) return;
    if (!comments.trim()) { setCommentError(true); return; }
    onApply({ booked: bookedValue, comment: comments.trim(), isManualOverride: isManualUserValue });
  };

  return (
    <MeterDetailsBlade
      title="Estimate"
      open={open}
      onClose={onClose}
      meterName={meter?.location}
      meterNumber={meter?.meter_number}
      monthYear={monthYear}
      monthLabel={monthLabel}
      onMonthChange={onMonthChange}
      handleApply={handleApplyClick}
      applyLabel="Apply Estimate"
      saving={saving}
      isDirty={true}
      readOnly={readOnly}
      saveFeedback={applyError ? { status: 'error', message: applyError } : null}
    >
      <BladeSection title="Prior Month">
        <div className={bladeStyles.inlineRow}>
          <div className={bladeStyles.inlineField}>
            <label className={bladeStyles.inlineLabel}>Net Gen (kWh)</label>
            <div className={bladeStyles.readonlyValue} data-testid="prior-net-gen">
              {lastMonthKWh == null ? '—' : Math.round(lastMonthKWh).toLocaleString()}
            </div>
          </div>

          <div className={bladeStyles.inlineField}>
            <label className={bladeStyles.inlineLabel}>Read Type</label>
            <div className={bladeStyles.readonlyValue} data-testid="prior-read-type">{meterData?.lastMonthMeterReadType ?? '—'}</div>
          </div>

          <div className={bladeStyles.inlineField}>
            <label className={bladeStyles.inlineLabel}>Estimation Factor</label>
            <div className={bladeStyles.readonlyValue} data-testid="estimation-factor">{estFactor ?? '—'}</div>
          </div>
        </div>
      </BladeSection>

      <BladeSection title="Estimate">
        <div className={bladeStyles.inlineRow}>
          <div className={bladeStyles.inlineField} style={{ alignItems: 'center' }}>
            <label className={bladeStyles.inlineLabel}>Manual Override</label>
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <input
                type="checkbox"
                data-testid="manual-user-value-checkbox"
                checked={isManualUserValue}
                disabled={readOnly}
                className={bladeStyles.checkboxInput}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setIsManualUserValue(checked);
                  if (checked && !bookedEstimate) setBookedEstimate(calculatedEstimate);
                }}
              />
            </div>
          </div>
          <div className={bladeStyles.inlineField}>
            <label className={bladeStyles.inlineLabel}>Estimate Value</label>
            <input
              className={bladeStyles.fieldInput}
              data-testid="calculated-estimate-input"
              value={displayedEstimate}
              onChange={(e) => { if (isManualUserValue && !readOnly) setBookedEstimate(e.target.value); }}
              readOnly={!isManualUserValue || readOnly}
              style={!isManualUserValue || readOnly ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : {}}
            />
          </div>
          <div className={bladeStyles.inlineField}>
            <label className={bladeStyles.inlineLabel}>
              Comments{!readOnly && <span className={bladeStyles.required}> *</span>}
            </label>
            <input
              className={`${bladeStyles.fieldInput}${commentError ? ` ${bladeStyles.error}` : ''}`}
              data-testid="comments-input"
              maxLength={200}
              value={comments}
              readOnly={readOnly}
              style={readOnly ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : {}}
              onChange={(e) => {
                if (!readOnly) {
                  const val = e.target.value.replace(/[^\x20-\x7E -￿\uD800-\uDFFF]/g, '');
                  setComments(val);
                  if (val.trim()) setCommentError(false);
                }
              }}
            />
            {commentError && <div className={bladeStyles.errorText} data-testid="comment-error">Comment is required</div>}
          </div>
        </div>
      </BladeSection>

      <BladeSection title={`Regional Estimate Factors — ${energySourceLabel}`}>
        <table className={bladeStyles.regionalTable} data-testid="regional-factors-table">
          <thead>
            <tr>
              <th>Region</th>
              <th>Last Month (kWh)</th>
              <th>Current Month (kWh)</th>
              <th>Estimation Factor</th>
            </tr>
          </thead>
          <tbody>
            {regionalData.length > 0 ? (
              regionalData.map((row) => (
                <tr key={row.region} data-testid="region-row" data-region={row.region}>
                  <td>
                    <button
                      type="button"
                      className={bladeStyles.regionLink}
                      onClick={() => navigate(`/estimation-details?energyType=${energySource?.toLowerCase()}&region=${row.region}&month=${monthYear}`)}
                      data-testid="region-link"
                    >
                      {row.region}
                    </button>
                  </td>
                  <td data-testid="region-value" data-column="last-month">{row.lastMonth.toLocaleString()}</td>
                  <td data-testid="region-value" data-column="current-month">{row.currentMonth.toLocaleString()}</td>
                  <td data-testid="region-value" data-column="factor"><span className={getFactorClassName(row.factor)}>{row.factor ?? '—'}</span></td>
                </tr>
              ))
            ) : (
              <tr data-testid="regional-empty">
                <td colSpan={4} style={{ textAlign: 'center', color: '#9ca3af' }}>
                  No regional data available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </BladeSection>
    </MeterDetailsBlade>
  );
};

export default EstimationBlade;
